// src/components/StudentPlanWidget.jsx
//
// PLAN S11c: the progress widget on the student dashboard - days left,
// sessions used this week (/3), projects (x/8), and which of the four
// tools are already used this week (view-only-this-week state, shown here
// as a display, not enforced here - enforcement is already live server-side
// via db/services/free_tier_gate.py). Reads GET /free-tier/status (S9).
import React, { useEffect, useState } from 'react';
import { baseUrl } from '../config';

const TOOL_LABELS = {
  script: 'Script Analysis',
  budget: 'Budgeting',
  casting: 'Casting Assistant',
  location: 'Location Scouting',
};
const ALL_TOOLS = ['script', 'budget', 'casting', 'location'];

function formatDate(iso) {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
}

const StudentPlanWidget = () => {
  const [status, setStatus] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) { setFailed(true); return; }
    (async () => {
      try {
        const res = await fetch(`${baseUrl}/free-tier/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) { setFailed(true); return; }
        setStatus(await res.json());
      } catch (_) {
        setFailed(true);
      }
    })();
  }, []);

  // Fail quiet: the rest of the dashboard still works without this widget.
  if (failed) return null;

  if (!status) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6 mb-6 animate-pulse" aria-hidden="true">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
        <div className="h-3 bg-gray-100 rounded w-2/3" />
      </div>
    );
  }

  if (status.state === 'not_started') {
    return (
      <div className="rounded-2xl border border-teal-200 bg-teal-50 p-6 mb-6">
        <p className="text-teal-800 font-semibold">Your free plan hasn't started yet</p>
        <p className="text-teal-700 text-sm mt-1">
          Your 2 months begin the first time you use a tool on one of your projects.
        </p>
        <p className="text-teal-700 text-sm mt-3">
          Projects: {status.projects_used}/{status.projects_limit}
        </p>
      </div>
    );
  }

  if (status.state === 'expired') {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 mb-6">
        <p className="text-amber-800 font-semibold">Your 2-month free plan has ended</p>
        <p className="text-amber-700 text-sm mt-1">
          Your projects and data are safe. Choose a plan to continue using the tools.
        </p>
      </div>
    );
  }

  if (status.state !== 'active') return null; // e.g. not_student - shouldn't reach here, fail quiet

  const unlockDate = formatDate(status.next_unlock_at);
  const usedThisWeek = new Set(status.tools_used_this_week || []);

  return (
    <div className="rounded-2xl border border-teal-200 bg-gradient-to-r from-teal-50 to-cyan-50 p-6 mb-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-teal-800 font-semibold">Your free plan</p>
        <p className="text-sm text-teal-700">
          {status.days_left} day{status.days_left === 1 ? '' : 's'} left
        </p>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div>
          <p className="text-xs text-teal-600 uppercase tracking-wide">Sessions this week</p>
          <p className="text-lg font-bold text-slate-800">{status.sessions_used}/{status.sessions_limit}</p>
        </div>
        <div>
          <p className="text-xs text-teal-600 uppercase tracking-wide">Projects</p>
          <p className="text-lg font-bold text-slate-800">{status.projects_used}/{status.projects_limit}</p>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-xs text-teal-600 uppercase tracking-wide mb-2">Tools this week</p>
        <div className="flex flex-wrap gap-2">
          {ALL_TOOLS.map((key) => {
            const used = usedThisWeek.has(key);
            return (
              <span
                key={key}
                className={
                  used
                    ? 'text-xs font-medium px-2.5 py-1 rounded-full border bg-white text-teal-400 border-teal-100'
                    : 'text-xs font-medium px-2.5 py-1 rounded-full border bg-white text-teal-700 border-teal-300'
                }
              >
                {TOOL_LABELS[key]}{used ? ' · used this week' : ''}
              </span>
            );
          })}
        </div>
        {unlockDate && usedThisWeek.size > 0 && (
          <p className="text-xs text-teal-600 mt-2">Unlocks again on {unlockDate}</p>
        )}
      </div>
    </div>
  );
};

export default StudentPlanWidget;
